import { TooltipHost } from '@fluentui/react';
import {
  Input,
  DataGrid,
  TableColumnDefinition,
  createTableColumn,
  Switch,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  FluentProvider,
} from '@fluentui/react-components';
import {
  IDynamicPerson,
  PeoplePicker,
  PeoplePickerProps,
  Person,
  PersonCardInteraction,
  PersonViewType,
} from '@microsoft/mgt-react';
import { v4 as uuidv4 } from 'uuid';

import { callFunction } from '../../lib/Helpers';
import { SubsUsersTableContainer } from './styles';
import { createRef, useRef, useState } from 'react';
import { BitButton } from '../BitButton';
import { MagnifyingGlass, Trash, X } from '@phosphor-icons/react';
import { Tag } from '../Tag';
import { TeamsUserCredential } from '@microsoft/teamsfx';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface SubsUsersTableProps {
  users: string[];
  admins: string[];
  owner: string;
  maxUsers: number;
  themeName?: string;
  teamsUserCredential?: TeamsUserCredential;
  onAdminsChanged: (admins: string[]) => void;
  onUsersChanged: (users: string[]) => void;
  onIsDirty: (isDirty: boolean) => void;
  onToNotifyUsersChanged: (users: string[]) => void;
}

export interface IDocument {
  key: string;
  name: string;
  value: string;
  isAdmin: boolean;
  isOwner: boolean;
}

export const SubsUsersTable = (props: SubsUsersTableProps) => {
  const pickerRef = createRef<PeoplePickerProps>();
  let isPersonLoaded = {} as any;

  const { t } = useTranslation('settings');

  const columns: TableColumnDefinition<IDocument>[] = [
    createTableColumn<IDocument>({
      columnId: 'user',
      compare: (a, b) => {
        return a.name.localeCompare(b.name);
      },
      renderHeaderCell: () => {
        return t('PANEL_TABLE_USER_COLUMN');
      },
      renderCell: (item) => {
        return (
          <Person
            onLoad={() => onLoadedPerson(item)}
            userId={item.key}
            view={PersonViewType.oneline}
            showPresence
            personCardInteraction={PersonCardInteraction.none}
          />
        );
      },
    }),
    createTableColumn<IDocument>({
      columnId: 'admin',
      compare: (a, b) => {
        return String(a.isAdmin).localeCompare(String(b.isAdmin));
      },

      renderHeaderCell: () => {
        return t('PANEL_TABLE_ADMIN_COLUMN');
      },
      renderCell: (item) => {
        return (
          <Switch
            id={item.key}
            hidden={item.isOwner}
            checked={item.isAdmin}
            onChange={() => onChangeAdminMode(item)}
          />
        );
      },
    }),
    createTableColumn<IDocument>({
      columnId: 'delete',
      renderHeaderCell: () => {
        return <Trash />;
      },
      renderCell: (item) => {
        return (
          <TooltipHost content="Unassign User">
            {!item.isOwner && <X onClick={() => onClickUnassignUser(item)} />}
          </TooltipHost>
        );
      },
    }),
  ];

  const [allItems, setAllItems] = useState(generateDocuments(props));
  const [isDirty, setIsDirty] = useState(false);
  const [addedUsers, setAddedUsers] = useState<IDocument[]>([]);

  const [items, setItems] = useState<IDocument[]>(allItems);
  const [isAboveLimit, setIsAboveLimit] = useState(false);
  const [refreshPerson, setRefreshPerson] = useState('initial');
  const [selectedPeople, setSelectedPeople] = useState<IDynamicPerson[]>([]);
  const [toNotifyUsers, setToNotifyUsers] = useState<string[]>([]);
  const deletedItems = useRef([] as string[]);

  function onLoadedPerson(item: any) {
    if (!isPersonLoaded[item.key]) {
      isPersonLoaded[item.key] = true;
      setRefreshPerson(uuidv4());
    }
  }

  function generateDocuments(props: SubsUsersTableProps) {
    const items: IDocument[] = [];
    props.users.forEach((user) => {
      items.push({
        key: user,
        name: user,
        value: user,
        isAdmin: props?.admins.find((usr) => usr === user) ? true : false,
        isOwner: user === props.owner ? true : false,
      });

      callFunction(
        true,
        'getUserProfile?{0}&uid=' + user,
        undefined,
        false,
        props.teamsUserCredential
      ).then((data) => {
        items[items.findIndex((value) => value.key === user)].name =
          data.displayName;
      });
    });

    return items;
  }

  function onChangeAdminMode(item: IDocument) {
    const index = items.findIndex((user) => user.key === item.key);
    const user = items[index];

    if (user) {
      const updatedUser = { ...user, isAdmin: !user.isAdmin };
      const newItems = [...items];
      newItems.splice(index, 1, updatedUser);

      setItems(newItems);

      const newAdmins: string[] = newItems
        .filter((value) => value.isAdmin === true)
        .map((e) => e.key);

      props.onAdminsChanged(newAdmins);
      props.onIsDirty(true);
      setIsDirty(true);
    }
  }

  function onClickUnassignUser(item: IDocument) {
    setToNotifyUsers(toNotifyUsers.filter((i) => i !== item.key));
    props.onToNotifyUsersChanged(toNotifyUsers.filter((i) => i !== item.key));
    if (item.isAdmin) onChangeAdminMode(item);
    deletedItems.current.push(item.key);
    const newUsers = allItems.filter(
      (item) => !deletedItems.current.includes(item.key)
    );
    setAllItems(newUsers);
    setItems(newUsers);
    props.onUsersChanged(newUsers.map((item) => item.key));
    props.onIsDirty(true);
    setIsDirty(true);
  }

  function onClickAddUsers() {
    if (selectedPeople) {
      const isImplicitContact = selectedPeople.find((user) => {
        const person = JSON.parse(JSON.stringify(user));
        return person.personType.subclass === 'ImplicitContact'
          ? person
          : undefined;
      });

      if (isImplicitContact) {
        toast.error(
          "You can't add a subscription for a non Microsoft Account."
        );
        return;
      }

      const newItems = [...allItems, ...addedUsers];
      const newAdddedUsers = [...addedUsers];
      const newToNotifyUsers = [...toNotifyUsers];

      if (selectedPeople.length > props.maxUsers - allItems.length) {
        setIsAboveLimit(true);
      } else {
        selectedPeople.forEach((person) => {
          if (!newItems.find((i) => i.key === person.id)) {
            const newUser = {
              key: person.id!,
              name: person.displayName!,
              value: person.id!,
              isAdmin: false,
              isOwner: false,
            };
            newItems.push(newUser);
            newAdddedUsers.push(newUser);
          }

          if (!newToNotifyUsers.find((value) => value === person.id)) {
            newToNotifyUsers.push(person.id!);
          }
        });

        if (pickerRef.current) pickerRef.current.selectedPeople = [];

        props.onUsersChanged(newItems.map((item) => item.key));
        props.onToNotifyUsersChanged(newToNotifyUsers);
        props.onIsDirty(true);

        setItems(newItems);
        setToNotifyUsers(newToNotifyUsers);
        setAddedUsers(newAdddedUsers);
        setSelectedPeople([]);
        setIsDirty(true);
      }
    }
  }

  function onFilterChanged(e: React.ChangeEvent) {
    const target = e.target as HTMLInputElement;
    const newItems = allItems.filter((item) =>
      item.name.toLowerCase().includes(target.value.toLowerCase())
    );

    setItems(newItems);
  }

  return (
    <SubsUsersTableContainer>
      {isDirty && <Tag color="orange" content={t('PANEL_CHANGES_NOT_SAVED')} />}
      <div className="pickerContainer">
        <PeoplePicker
          ref={pickerRef}
          className={props.themeName !== 'default' ? 'mgt-dark' : ''}
          selectionChanged={(e) => setSelectedPeople((e as CustomEvent).detail)}
        />

        <div className="pickerFooter">
          <div className="licences">
            {isAboveLimit &&
              (props.maxUsers - allItems.length === 0 ? (
                <span className="nonAvailable">
                  {t('PANEL_NO_LICENCES_AVAILABLE')}
                </span>
              ) : (
                <span>
                  {t('PANEL_LICENCES_REMAINING', {
                    licences: props.maxUsers - allItems.length,
                  })}
                </span>
              ))}

            <span className="licences">
              {allItems.length} {t('SUBSCRIPTIONS_LICENCES_AMOUNT')}{' '}
              {props.maxUsers} {t('SUBSCRIPTIONS_LICENCES_ASSIGNED')}
            </span>
          </div>
          <BitButton label={t('PANEL_ADD_BUTTON')} action={onClickAddUsers} />
        </div>
      </div>

      <div className="tableContainer">
        <Input
          className="filterInput"
          contentAfter={<MagnifyingGlass weight="duotone" />}
          placeholder={t('PANEL_FILTER_PLACEHOLDER')}
          onChange={onFilterChanged}
          style={{ marginBottom: '1rem' }}
        />
        <FluentProvider>
          <DataGrid
            items={items}
            columns={columns}
            sortable
            resizableColumns
            columnSizingOptions={{
              user: { defaultWidth: 180, minWidth: 180 },
              admin: { defaultWidth: 60, minWidth: 60 },
              delete: { defaultWidth: 40, minWidth: 40 },
            }}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<IDocument>>
              {({ item, rowId }) => (
                <DataGridRow<IDocument> key={rowId}>
                  {({ renderCell }) => (
                    <DataGridCell>{renderCell(item)}</DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </FluentProvider>
      </div>
    </SubsUsersTableContainer>
  );
};
